import React from 'react';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { X } from 'lucide-react';

export default function TransactionHistoryModal({ isOpen, onClose, kidId }) {
  const { family, allowanceTransactions } = useFamilyContext();

  if (!isOpen || !kidId) return null;

  const kid = family?.kids?.find(k => k.id === kidId);
  const transactions = allowanceTransactions[kidId] || [];

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'job_completion':
        return '💼';
      case 'deduction':
        return '−';
      case 'addition':
      default:
        return '+';
    }
  };

  const getTransactionClass = (type) => {
    switch (type) {
      case 'job_completion':
        return 'activity-icon job';
      case 'deduction':
        return 'activity-icon remove';
      case 'addition':
      default:
        return 'activity-icon add';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h2 className="text-xl font-bold text-slate-900">
            {kid?.name}'s Transaction History
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-stone-100 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No transactions yet
              </h3>
              <p className="text-stone-500">
                Transaction history will appear here as {kid?.name} earns and spends allowance.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map(tx => (
                <div key={tx.id} className="activity-item">
                  <div className={getTransactionClass(tx.type)}>
                    {getTransactionIcon(tx.type)}
                  </div>
                  <div className="activity-content">
                    <div className="activity-text">{tx.reason}</div>
                    <div className="activity-time">{formatDate(tx.created_at)}</div>
                  </div>
                  <div className={`activity-amount ${tx.type === 'deduction' ? 'negative' : ''}`}>
                    {tx.type === 'deduction' ? '−' : '+'}
                    {formatMoney(Math.abs(tx.amount))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-stone-200">
          <button
            onClick={onClose}
            className="w-full bg-slate-900 text-white py-3 px-4 rounded-lg font-semibold hover:bg-slate-950 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
} 